import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored token and fetch current user
    const token = localStorage.getItem("tarewu_token");
    if (token) {
      api
        .me()
        .then((res) => {
          if (res && res.data) {
            const user = res.data;
            localStorage.setItem("tarewu_user", JSON.stringify(user));
            setUser(user);
          } else {
            localStorage.removeItem("tarewu_user");
            localStorage.removeItem("tarewu_token");
            setUser(null);
          }
        })
        .catch(() => {
          localStorage.removeItem("tarewu_user");
          localStorage.removeItem("tarewu_token");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleUnauthorized = (event) => {
      setUser(null);
      localStorage.removeItem("tarewu_user");
      localStorage.removeItem("tarewu_token");
      sessionStorage.setItem(
        "tarewu_auth_message",
        event.detail?.message || "Please sign in again.",
      );
    };
    window.addEventListener("tarewu:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("tarewu:unauthorized", handleUnauthorized);
  }, []);

  const login = async (email, password) => {
    const res = await api.login(email, password);
    if (!res || !res.data) throw new Error("Login failed");
    if (res.data.otp_required) return res.data;
    return persistLogin(res.data);
  };
  const persistLogin = (data) => {
    if (!data?.token || !data?.user) throw new Error("Login failed");
    const token = data.token;
    const user = data.user;
    // Normalize role to slug (in case backend returns role_id only)
    if (data.user && data.user.role) {
      user.role = data.user.role;
    }
    localStorage.setItem("tarewu_token", token);
    localStorage.setItem("tarewu_user", JSON.stringify(user));
    setUser(user);
    return user;
  };
  const verifyElectionLoginOTP = async (challenge, otp) => {
    const res = await api.verifyElectionLoginOTP(challenge, otp);
    return persistLogin(res.data);
  };
  const logout = async () => {
    // Clear the client session immediately so authenticated background effects
    // stop before the server revokes the token.
    const request = api.logout();
    setUser(null);
    localStorage.removeItem("tarewu_user");
    localStorage.removeItem("tarewu_token");
    try {
      await request;
    } catch (e) {
      // ignore
    }
  };

  const updateUser = (userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
    localStorage.setItem(
      "tarewu_user",
      JSON.stringify({ ...user, ...userData }),
    );
  };

  const hasRole = (roles) => {
    if (!user) return false;
    const userRoles = Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role];
    if (typeof roles === "string") {
      return userRoles.includes(roles);
    }
    return roles.some((role) => userRoles.includes(role));
  };

  const hasPermission = (permission) => {
    if (!user || !permission) return false;
    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    if (permissions.includes('*') || permissions.includes(permission))
      return true;
    const domain = permission.split(".")[0];
    return permissions.includes(`${domain}.*`);
  };

  const value = {
    user,
    loading,
    login,
    verifyElectionLoginOTP,
    logout,
    updateUser,
    hasRole,
    hasPermission,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
